import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { ISpace } from '../../../../types';
import { getApiV1Spaces } from '@konova/api';
import { useSpaceDomain } from '@common/hooks/use-space-domain';
import { useAsync } from '@konova/ui/use-async';
import { LoadingIndicator } from '@konova/ui/loading-indicator';

export type SpaceContextType = {
  space: ISpace | null;
  isLoading: boolean;
  setSpace: (space: ISpace) => void;
  spacesWhereUserIsAdmin: ISpace[];
  reload: () => void;
  setSpacesWhereUserIsAdmin: (space: ISpace[]) => void;
};

export const SpaceContext = createContext<SpaceContextType>({
  space: null,
  isLoading: false,
  spacesWhereUserIsAdmin: [],
  setSpace: () => {
    throw new Error('setSpace function must be overridden');
  },
  setSpacesWhereUserIsAdmin: () => {
    throw new Error('setSpacesWhereUserIsAdmin function must be overridden');
  },
  reload: () => {
    throw new Error('reload function must be overridden');
  }
});

export const useSpaceContext = () => useContext(SpaceContext);

export const SpaceContextProvider: FC<
  PropsWithChildren<{ space?: ISpace | null }>
> = ({ children, space: initialSpace }) => {
  const [space, setSpace] = useState<ISpace | null>(initialSpace ?? null);
  const [spacesWhereUserIsAdmin, setSpacesWhereUserIsAdmin] = useState<ISpace[]>([]);
  const domain = useSpaceDomain();

  const load = useAsync<() => Promise<ISpace | null>>(async () => {
    const response = await getApiV1Spaces(({
      query: {
        'filter[custom_domain]': domain,
        include: 'loginSettings'
      }
    }));
    return (response.data?.data?.[0] as unknown as ISpace) ?? null;
  }, {
    defaultValue: initialSpace ?? null
  });

  useEffect(() => {
    load.run();
  }, [domain]);

  if (load.isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <SpaceContext.Provider value={{
      space: space ?? load.data ?? null,
      isLoading: load.isLoading,
      spacesWhereUserIsAdmin,
      setSpace,
      setSpacesWhereUserIsAdmin,
      reload: () => load.run(),
    }}>
      {children}
    </SpaceContext.Provider>
  );
};